import { financialItemPath } from 'src/financial/financial-data/financial-item/financialItemPaths';
import { v4 as uuidv4 } from 'uuid';
import { NOTIFICATION_CARDS, notificationGroupLabel } from 'src/notifications/notificationCards';
import type {
  Notification,
  NotificationPayload,
  NotificationType,
} from 'src/notifications/types/Notification';
import { formatShortDate, formatTime } from 'src/utils/dateFormatting';

// Completions are point-in-time information, not outstanding work, so the dashboard leaves
// them to the bell.
const COMPLETION_TYPES: ReadonlySet<NotificationType> = new Set<NotificationType>([
  'UPLOAD_DETECTION_COMPLETE',
  'SUITABILITY_REPORT_COMPLETE',
  'MEETING_AGENDA_COMPLETE',
  'MEETING_NOTE_COMPLETE',
]);

export function outstandingNotifications(notifications: Notification[]): Notification[] {
  return notifications.filter(notification => !COMPLETION_TYPES.has(notification.payload.type));
}

export function outstandingByGroup(notifications: Notification[]): [string, Notification[]][] {
  const groups = new Map<string, Notification[]>();
  for (const notification of outstandingNotifications(notifications)) {
    const label = notificationGroupLabel(notification.payload.type);
    const existing = groups.get(label) ?? [];
    existing.push(notification);
    groups.set(label, existing);
  }
  const ordered: [string, Notification[]][] = [];
  for (const { groupLabel } of Object.values(NOTIFICATION_CARDS)) {
    const group = groups.get(groupLabel);
    if (group && !ordered.some(([label]) => label === groupLabel)) {
      ordered.push([groupLabel, group]);
    }
  }
  return ordered;
}

type DashboardRowDescriptor = {
  detail: string | null;
  // Shown in place of the client name when the notification has no linked client.
  fallback: string | null;
  to: string;
};

// Destinations mirror each type's card in src/notifications/components/cards/.
export function dashboardRow(payload: NotificationPayload): DashboardRowDescriptor | null {
  switch (payload.type) {
    case 'SUITABILITY_REVIEW_DUE':
      return {
        detail: payload.last_review_at
          ? `Last review ${formatShortDate(payload.last_review_at)}`
          : 'Never reviewed',
        fallback: null,
        to: `/client/${payload.client_id}`,
      };
    case 'PROTECTION_EXPIRING':
      return {
        detail: `Expires ${formatShortDate(payload.term_end_date)}`,
        fallback: null,
        to: financialItemPath(payload.client_id, 'Protection', payload.protection_id),
      };
    case 'MEETING_WITHOUT_NOTE':
      return {
        detail: `${formatShortDate(payload.meeting_date)}, ${formatTime(payload.meeting_date)}`,
        fallback: payload.meeting_title,
        to: `/meetings/${payload.calendar_event_id}/meeting-note`,
      };
    case 'MEETING_WITHOUT_AGENDA':
      return {
        detail: `${formatShortDate(payload.meeting_date)}, ${formatTime(payload.meeting_date)}`,
        fallback: payload.meeting_title,
        to: payload.client_id
          ? `/client/${payload.client_id}/meetings/upcoming/${payload.calendar_event_id}`
          : `/meetings/${payload.calendar_event_id}`,
      };
    case 'DETECTION_UNREVIEWED':
      return {
        detail: payload.file_name,
        fallback: null,
        to: `/client/${payload.client_id}/detected-entities?fileId=${payload.data_upload_file_id}&fileName=${encodeURIComponent(payload.file_name ?? '')}&guid=${uuidv4()}`,
      };
    default:
      return null;
  }
}
